import React from "react"
import PropTypes from "prop-types"
import styled, { css } from "styled-components"
import { useIntl } from "gatsby-plugin-react-intl"
import { useStaticQuery, graphql } from "gatsby"

import Layout from "./Layout"
import { Container, Row, Col } from "../components/Grid"
import Hero, {
  Header as HeroHeader,
  Text as Herotext,
} from "../components/Hero"
import Link from "../components/Link"
import Spacer from "../components/Spacer"
import Button from "../components/Button"
import SocialShare from "../components/SocialShareVertical"
import TestimonialHero from "../components/TestimonialHero"
import StyledTeaser from "../components/Teaser"
import PublicationDownload from "../components/publications/PublicationDownload"

import { media, mixins } from "../styles"
import { headerHeightLg } from "../components/Header"

const HeroContainer = styled.div`
  ${(props) =>
    !props.headerWide &&
    css`
      ${HeroHeader} {
        @media ${media.xl} {
          padding-left: 60px;
          padding-right: 60px;
        }
      }
    `}

  ${Herotext} {
    @media ${media.xl} {
      margin-top: 0px;
    }

    @media ${media.xxl} {
      margin-bottom: 35px;
      margin-top: 70px;
    }
  }
`

const HeroButton = styled.div`
  position: relative;

  @media ${media.xx} {
    margin-bottom: 30px;
  }
`

const Share = styled.div`
  text-align: center;

  @media ${media.md} {
    margin-top: -90px;
  }

  @media ${media.lg} {
    position: sticky;
    top: ${headerHeightLg + 20}px;
  }
`

const CtaRow = styled.div`
  margin: 0 auto;
  max-width: 600px;
  position: relative;
  text-align: center;

  @media ${media.xl} {
    max-width: 900px;
  }

  h2 {
    ${mixins.title3}
  }

  h3 {
    ${mixins.title5}
  }
`

const Related = styled.div`
  background: ${({ theme }) => theme.greyLight};
  padding: 50px 0;

  @media ${media.md} {
    padding: 60px 0 80px;
  }

  h3 {
    ${mixins.title5}
    color: ${({ theme }) => theme.sage};
    text-align: center;
    margin: 0 0 25px;

    @media ${media.xl} {
      margin: 0 0 65px;
    }
  }

  @media print {
    display: none;
  }
`

const RelatedHeader = styled.h3`
  ${mixins.title5}
  color: ${({ theme }) => theme.sage};
  text-align: center;
  margin: 0 0 25px;

  @media ${media.xl} {
    margin: 0 0 65px;
  }
`

const RelatedList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;

  @media ${media.sm} {
    flex-flow: row wrap;
    justify-content: space-between;
  }
`

const RelatedTeaser = styled(StyledTeaser)`
  @media ${media.sm} {
    width: calc(50% - 15px);
  }

  @media ${media.md} {
    width: calc(33.3333333% - 20px);
  }
`

const Download = ({ publication }) => {
  return (
    <PublicationDownload
      id={publication.publicationId}
      file={publication.download}
      publicationType={publication.type}
    />
  )
}

const PublicationCustomLayout = ({
  children,
  headerWide,
  heroIllustration,
  publication,
  relatedPublications,
}) => {
  const intl = useIntl()

  const slug = `publications/${publication.slug}`
  const subheader =
    publication.subheader ||
    intl.formatMessage({
      id: `publications.categories.${publication.type}`,
    })

  const data = useStaticQuery(graphql`
    query PublicationCustomLayoutQuery {
      site {
        siteMetadata {
          title
          siteUrl
        }
      }
    }
  `)

  const publicationType = intl.formatMessage({
    id: `publications.categories.${publication.type}`,
  })

  const capitalizedType =
    intl.locale === "en" ? publicationType.toLowerCase() : publicationType

  return (
    <Layout pageId={slug}>
      <HeroContainer headerWide={headerWide}>
        <Hero
          subHeader={subheader}
          header={publication.title}
          text={publication.intro?.childMarkdownRemark.html}
          illustration={heroIllustration}
          heroExtraButton={
            publication.externalUrl ? (
              <HeroButton>
                <Button arrow as={Link} highlight to={publication.externalUrl}>
                  {intl.formatMessage(
                    { id: "publications.get" },
                    {
                      publication: capitalizedType,
                    }
                  )}
                </Button>
              </HeroButton>
            ) : publication.download?.publicUrl ? (
              <HeroButton>
                <Download publication={publication} />
              </HeroButton>
            ) : null
          }
        />
      </HeroContainer>
      <Spacer height={80} md={90} lg={30} />
      <Container>
        <Row>
          <Col lg="1">
            <Share>
              <SocialShare
                label={intl.formatMessage(
                  { id: `publications.share` },
                  {
                    publication: capitalizedType,
                  }
                )}
                text={publication.socialShareText || publication.title}
                url={`${data.site.siteMetadata.siteUrl}/${intl.locale}/${slug}/`}
              />
            </Share>
          </Col>
          <Col lg="10">{children}</Col>
        </Row>
        <Spacer height={80} md={90} xl={100} />
        {publication.testimonials?.length > 0 ? (
          <TestimonialHero testimonials={publication.testimonials} />
        ) : null}
        <Spacer height={80} md={100} xl={130} />
        {publication.externalUrl || publication.download?.publicUrl ? (
          <>
            <CtaRow>
              <h3>{subheader}</h3>
              <Spacer xl={8} />
              <h2>{publication.ctaHeader || publication.title}</h2>
              <Spacer height={5} lg={20} xl={37} />
              {publication.externalUrl ? (
                <Button arrow as={Link} highlight to={publication.externalUrl}>
                  {intl.formatMessage(
                    { id: "publications.get" },
                    {
                      publication: capitalizedType,
                    }
                  )}
                </Button>
              ) : publication.download?.publicUrl ? (
                <Download publication={publication} />
              ) : null}
            </CtaRow>
            <Spacer height={80} md={100} xl={145} />
          </>
        ) : null}
      </Container>
      {relatedPublications ? (
        <Related>
          <Container>
            <Row>
              <Col xl="8" xlOffset="2">
                <RelatedHeader>
                  {intl.formatMessage({
                    id: "knowledge.furtherPublications",
                  })}
                </RelatedHeader>
                <RelatedList>
                  {relatedPublications.map((relatedPublication) => (
                    <RelatedTeaser
                      key={relatedPublication.slug}
                      to={`/publications/${relatedPublication.slug}/`}
                      header={relatedPublication.title}
                      image={relatedPublication.image}
                      compact
                    />
                  ))}
                </RelatedList>
              </Col>
            </Row>
          </Container>
        </Related>
      ) : null}
    </Layout>
  )
}

PublicationCustomLayout.propTypes = {
  children: PropTypes.node.isRequired,
  headerWide: PropTypes.bool,
  heroIllustration: PropTypes.node.isRequired,
  publication: PropTypes.object.isRequired,
}

export default PublicationCustomLayout
